import { useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { RouterLink } from "src/components/router-link";
import { Seo } from "src/components/seo";
import { usePageView } from "src/hooks/use-page-view";
import { paths } from "src/paths";
import { getServicePlanIllustration } from "src/sections/pricing/service-plan-card";

interface ServiceCard {
  id: string;
  category: string;
  title: string;
  description: string;
  price: string;
  details: string[];
  buttonText: string;
  buttonVariant: "contained" | "outlined";
  illustrationBg: string;
  link: string;
}

export const MedicalCoachingPage: FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));
  usePageView();

  useEffect(() => {
    const id = window.location.hash.replace("#", "");
    if (!id) {
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const services: ServiceCard[] = [
    {
      id: "onboarding",
      category: "Onboarding",
      title: "Technical guidance",
      description: "Less stress about the technical setup and configuration",
      price: "",
      details: [
        "Onboarding session",
        "CGM, pump, and app setup and configuration",
        "Help with technical errors and questions",
        "24/7 WhatsApp Helpdesk",
        "Free intake session",
      ],
      buttonText: "Book now",
      buttonVariant: "outlined",
      illustrationBg: "#FCE7F3", // Light pink/peach
      link: "https://cal.com/team/opensourcelinic/onboarding",
    },
    {
      id: "coaching",
      category: "Coaching",
      title: "Medical guidance",
      description: "So you can focus on living your best life",
      price: "",
      details: [
        "Guided basal, ISF/dyn ISF, and CR configuration",
        "Help with medical questions",
        "24/7 WhatsApp Helpdesk",
        "Free intake session",
        "Medically supervised",
      ],
      buttonText: "Book now",
      buttonVariant: "outlined",
      illustrationBg: "#FEF3C7", // Light yellow/orange
      link: "https://cal.com/team/opensourcelinic/medical-coaching",
    },
    {
      id: "carbless",
      category: "Coaching",
      title: "CarblessLooping",
      description: "CarblessLoopingDescription",
      price: "CarblessLoopingPrice",
      details: [
        "CarblessLoopingDuration",
        "CarblessLiveSessions",
        "CarblessWhatsApp",
        "CarblessNoSuccessMoneyBack",
        "Medically supervised",
      ],
      buttonText: "Book now",
      buttonVariant: "outlined",
      illustrationBg: "#D1FAE5",
      link: "https://cal.com/team/opensourcelinic/medical-coaching",
    },
    {
      id: "home-visit",
      category: "Visit",
      title: "Home visit",
      description: "Home visit for setting up your loop (only in Netherlands)",
      price: "€100 plus travel cost",
      details: [
        "Help connecting a smartwatch to AAPS",
        "Help setting up your looping app (AndroidAPS, Trio)",
        "Help configuring Nightscout",
        "Not satisfied, money back",
      ],
      buttonText: "Book now",
      buttonVariant: "outlined",
      illustrationBg: "#CFFAFE",
      link: "https://cal.com/team/opensourcelinic/medical-coaching",
    },
    {
      id: "loop-config-short",
      category: "Coaching",
      title: "Loop configuration short",
      description: "Help configuring your looping app with the right settings",
      price: "€400",
      details: [
        "Temporary targets and overrides",
        "Dynamic ISF",
        "Carb ratio, ISF, basal, and DIA",
        "WhatsApp 24/7 support",
        "Not satisfied, money back",
        "Medically supervised",
        "1 month, 1–2 video calls per week",
      ],
      buttonText: "Book now",
      buttonVariant: "outlined",
      illustrationBg: "#EDE9FE",
      link: "https://cal.com/team/opensourcelinic/medical-coaching",
    },
    {
      id: "loop-config-long",
      category: "Coaching",
      title: "Loop configuration long",
      description: "Help configuring your looping app with the right settings",
      price: "€800",
      details: [
        "Temporary targets and overrides",
        "Dynamic ISF",
        "Carb ratio, ISF, basal, and DIA",
        "WhatsApp 24/7 support",
        "Not satisfied, money back",
        "Medically supervised",
        "2 months, 1–2 video calls per week",
      ],
      buttonText: "Book now",
      buttonVariant: "outlined",
      illustrationBg: "#FFEDD5",
      link: "https://cal.com/team/opensourcelinic/medical-coaching",
    },
  ];

  return (
    <>
      <Seo
        title="Diabetes Coaching and Technical Setup"
        description="Book technical onboarding or medical coaching for open-source looping, including basal rates, ISF, carb ratios, and a 24/7 helpdesk."
        type="website"
      />
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          py: "120px",
          minHeight: "100vh",
        }}
      >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography
            component="h1"
            variant="h1"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: "bold",
              mb: 2,
              fontSize: smDown ? "2rem" : "3rem",
              lineHeight: 1.2,
            }}
          >
            {t("Coaching")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: "1.125rem",
              maxWidth: "600px",
              mx: "auto",
            }}
          >
            {t("Personalized support for your diabetes management journey")}
          </Typography>
        </Box>

        {/* Service Cards */}
        <Grid container spacing={4}>
          {services.map((service) => (
            <Grid xs={12} sm={6} md={4} key={service.id}>
              <Box
                id={service.id}
                sx={{
                  scrollMarginTop: 96,
                  backgroundColor: "#FFFFFF",
                  borderRadius: 3,
                  p: 4,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                  border: "1px solid #F3F4F6",
                  transition:
                    "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                {/* Illustration */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    mb: 3,
                  }}
                >
                  {getServicePlanIllustration(service.id)}
                </Box>

                {/* Category Tag */}
                <Box sx={{ mb: 2, display: "flex", justifyContent: "center" }}>
                  <Chip
                    label={t(service.category)}
                    size="small"
                    sx={{
                      backgroundColor: "#F3F4F6",
                      color: "#6B7280",
                      fontWeight: "500",
                      fontSize: "0.75rem",
                    }}
                  />
                </Box>

                {/* Title */}
                <Typography
                  variant="h5"
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: "bold",
                    mb: 2,
                    textAlign: "center",
                    lineHeight: 1.3,
                  }}
                >
                  {t(service.title)}
                </Typography>

                {/* Description */}
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    mb: 3,
                    textAlign: "center",
                    lineHeight: 1.6,
                  }}
                >
                  {t(service.description)}
                </Typography>

                {/* Divider */}
                <Box
                  sx={{
                    height: "1px",
                    backgroundColor: "#E5E7EB",
                    mb: 3,
                  }}
                />

                {/* Details List */}
                <Box sx={{ mb: 4 }}>
                  {service.details.map((detail, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        mb: 1,
                      }}
                    >
                      <ArrowForwardIcon
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: "16px",
                          mr: 1,
                          mt: "2px",
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: "0.875rem",
                        }}
                      >
                        {t(detail)}
                      </Typography>
                    </Box>
                  ))}
                  {/* Pricing */}
                  {service.price && (
                    <Typography
                      variant="h6"
                      sx={{
                        color: theme.palette.text.primary,
                        fontWeight: "bold",
                        mt: 2,
                        textAlign: "center",
                      }}
                    >
                      {t(service.price)}
                    </Typography>
                  )}
                </Box>

                {/* CTA Button */}
                <Box sx={{ mt: "auto" }}>
                  <Button
                    href={service.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant={service.buttonVariant}
                    fullWidth
                    size="large"
                    sx={{
                      backgroundColor:
                        service.buttonVariant === "contained"
                          ? theme.palette.primary.main
                          : "transparent",
                      color:
                        service.buttonVariant === "contained"
                          ? theme.palette.primary.contrastText
                          : theme.palette.text.primary,
                      borderColor:
                        service.buttonVariant === "outlined"
                          ? "#E5E7EB"
                          : "transparent",
                      borderRadius: 2,
                      textTransform: "none",
                      fontSize: "1rem",
                      fontWeight: "600",
                      py: 1.5,
                      "&:hover": {
                        backgroundColor:
                          service.buttonVariant === "contained"
                            ? theme.palette.primary.dark
                            : "#F9FAFB",
                        borderColor:
                          service.buttonVariant === "outlined"
                            ? "#D1D5DB"
                            : "#E5E7EB",
                      },
                    }}
                  >
                    {t(service.buttonText)}
                  </Button>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Typography variant="body2" sx={{ mt: 6, textAlign: "center" }}>
          <Link
            component={RouterLink}
            href={paths.diabetesCoachingNl}
            underline="hover"
            color="text.secondary"
          >
            {t("Read more about diabetes coaching in the Netherlands")}
          </Link>
        </Typography>
      </Container>
      </Box>
    </>
  );
};

export default MedicalCoachingPage;
